import APP_CONSTANTS, {
  PRODUCTS,
  SESSION_KEYS
} from '../lib/constants/appConstants';
import {
  ITemplateOptions,
  ITemplateValues
} from '../interfaces/TemplateDataInterface';
import {
  AdditionalInputs,
  ModifiedInputs,
  Package,
  PricingQuestion,
  PricingStepConfig
} from '../interfaces/Types';
import { Offer } from '../interfaces/Types';
import { ageResolver } from '../services/CustomerService';
import MainContainer from '../../../payment-se/src/containers/MainContainers';

export class APIHelper {
  static getSaveOfferPayload = (
    offerInfo: Offer,
    selectedPackage?: Package,
    selectedAdditionalPackages?: Package[],
    isPlusPacketSelected?: boolean,
    productName?: string,
    email?: string
  ) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return {
      headers: {
        Applikasjon: APP_CONSTANTS.application
      },
      body: {
        cpr: offerInfo.cprNumber,
        channel: customerInfo?.channel
          ? customerInfo.channel
          : APP_CONSTANTS.channel,
        agreementNumber: offerInfo.agreementNumber,
        policyNumber: offerInfo.policyNumber,
        product: offerInfo.productName,
        coveragePacketSelected: selectedPackage?.packageName || '',
        pluspaketCoverages: APIHelper.getAdditionalPacketsSelected(
          selectedAdditionalPackages,
          productName,
          isPlusPacketSelected
        ),
        email: email ? email : ''
      }
    };
  };

  static getAdditionalPacketsSelected = (
    selectedAdditionalPackages?: Package[],
    productName?: string,
    isPlusPacketSelected?: boolean
  ) => {
    let selectedAddons: string[] = [];
    switch (productName) {
      case PRODUCTS.CAR:
      case PRODUCTS.HOME:
        if (selectedAdditionalPackages) {
          selectedAdditionalPackages.map((item: Package) => {
            selectedAddons.push(item.plusPackagetype);
          });
        }
        break;
    }
    return selectedAddons;
  };

  static getTemplatePayload = (product: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return {
      headers: {
        Applikasjon: APP_CONSTANTS.application
      },
      body: {
        product: product,
        channel: customerInfo?.channel
          ? customerInfo.channel
          : APP_CONSTANTS.channel
      }
    };
  };

  static getEligibilityCheckAttributes = (
    product: string,
    customerInfo: any
  ) => {
    let attributeList = [];
    switch (product) {
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        if (customerInfo && customerInfo.zipCode) {
          attributeList.push({
            key: 'zip',
            value: customerInfo.zipCode
          });
        }
        break;
      default:
        attributeList = [];
    }
    return attributeList;
  };

  static getCustomerEligibilityPayload = (product: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return {
      headers: {
        Applikasjon: APP_CONSTANTS.application
      },
      body: {
        product: product,
        channel: customerInfo?.channel
          ? customerInfo.channel
          : APP_CONSTANTS.channel,
        cpr: customerInfo?.cprNumber ? customerInfo.cprNumber : '',
        attributeList: APIHelper.getEligibilityCheckAttributes(
          product,
          customerInfo
        )
      }
    };
  };

  static getDefaultCreatePayload = (product: string, cprNumber: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return {
      cpr: cprNumber,
      channel: customerInfo?.channel
        ? customerInfo.channel
        : APP_CONSTANTS.channel,
      product: product
    };
  };
  static getAccidentCreateOfferPayload = (templateData: PricingStepConfig[]) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.ACCIDENT,
        customerInfo.cprNumber
      );
      const templateQuestions = templateData.flatMap(
        (template) => template.questions
      );

      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'coverageAmount',
            value: APIHelper.getDefaultValueForQuestions(
              templateQuestions,
              'coverageAmount'
            ) || '1500000'
          }
        ]
      };
    }
  };
  static getMcCreateOfferPayload = (templateData: PricingStepConfig[]) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.MC,
        customerInfo.cprNumber
      );
      const templateQuestions = templateData.flatMap(
        (template) => template.questions
      );

      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'regPlate',
            value: customerInfo.registrationNumber
          },
          {
            key: 'mcType',
            value:
              APIHelper.getSelectedValueForQuestions(
                templateQuestions,
                'mcType'
              )
          },
          {
            key: 'driverUnder24',
            value: APIHelper.getSelectedValueForQuestions(
              templateQuestions,
              'driverUnder24'
            )
          }
        ]
      };
    }
  };

  static getMopedCreateOfferPayload = (templateData: PricingStepConfig[]) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.MOPED,
        customerInfo.cprNumber
      );
      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'regPlate',
            value: customerInfo.registrationNumber
          }
        ]
      };
    }
  };

  static getCarCreateOfferPayload = (templateData: PricingStepConfig[]) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.CAR,
        customerInfo.cprNumber
      );
      const templateQuestions = templateData.flatMap(
        (template) => template.questions
      );

      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'regPlate',
            value: customerInfo.registrationNumber
          },
          {
            key: 'yearlyKm',
            value:
              APIHelper.getDefaultValueForQuestions(
                templateQuestions,
                'yearlyKm'
              ) || '10'
          },
          {
            key: 'vagnskadaSelfRisk',
            value: APIHelper.getDefaultValueForQuestions(
              templateQuestions,
              'vagnskadaSelfRisk'
            )
          },
          {
            key: 'delkaskoSelfRisk',
            value: APIHelper.getDefaultValueForQuestions(
              templateQuestions,
              'delkaskoSelfRisk'
            )
          },
          {
            key: 'driverUnder24',
            value: APIHelper.isDriverAgeBelow24(customerInfo.cprNumber)
              ? '2'
              : '1'
          }
        ]
      };
    }
  };

  static getHomeCreateOfferPayload = (templateData: PricingStepConfig[]) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.HOME,
        customerInfo.cprNumber
      );
      const templateQuestions = templateData.flatMap(
        (template) => template.questions
      );
      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'zip',
            value: customerInfo.zipCode
          },
          {
            key: 'area',
            value:
              APIHelper.getSelectedValueForQuestions(
                templateQuestions,
                'area'
              ) || APIHelper.getSelectedValueFromSession('area')
          },
          {
            key: 'noOfPeople',
            value: APIHelper.getSelectedValueFromSession('noOfPeople')
          },
          {
            key: 'houseType',
            value: customerInfo.houseType
          },
          {
            key: 'selfRiskDeductible',
            value: APIHelper.getDefaultValueForQuestions(
              templateQuestions,
              'selfRiskDeductible'
            )
          },
          {
            key: 'otherAddress',
            value: customerInfo.customerAddress
          }
        ]
      };
    }
  };

  static getVillaCreateOfferPayload = () => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.VILLA,
        customerInfo.cprNumber
      );
      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'zip',
            value: customerInfo.zipCode || ''
          },
          {
            key: 'area',
            value: APIHelper.getSelectedValueFromSession('area')
          },
          {
            key: 'energyType',
            value: APIHelper.getSelectedValueFromSession('energyType')
          },
          {
            key: 'inspectionYear',
            value: APIHelper.getSelectedValueFromSession('inspectionYear')
          },
          {
            key: 'builtYear',
            value: APIHelper.getSelectedValueFromSession('builtYear')
          },
          {
            key: 'roofConstruction',
            value: APIHelper.getSelectedValueFromSession('roofConstruction')
          },
          {
            key: 'basement',
            value: APIHelper.getSelectedValueFromSession('basement')
          },
          {
            key: 'noOfFloors',
            value: APIHelper.getSelectedValueFromSession('noOfFloors')
          },
          {
            key: 'noOfPeople',
            value: APIHelper.getSelectedValueFromSession('noOfPeople')
          },
          {
            key: 'otherAddress',
            value: customerInfo.customerAddress
          },
          {
            key: 'propertyId',
            value: customerInfo.propertyId
          }
        ],
        extraBuildingList:
          APIHelper.getSelectedValueFromSession('extraBuildingList')
      };
    }
  };

  static getPPICreateOfferPayload = (templateData: PricingStepConfig[]) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    if (customerInfo.cprNumber) {
      const defaultRequestParams = APIHelper.getDefaultCreatePayload(
        PRODUCTS.PPI,
        customerInfo.cprNumber
      );
      const templateQuestions = templateData.flatMap(
        (template) => template.questions
      );

      return {
        ...defaultRequestParams,
        attributeList: [
          {
            key: 'coverageAmount',
            value: APIHelper.getDefaultValueForQuestions(
              templateQuestions,
              'coverageAmount'
            ) || '4000'
          }
        ]
      };
    }
  };

  static getDefaultValueForQuestions = (
    questions: PricingQuestion[],
    keyName: string
  ) => {
    const questionDetails = questions.find(
      (question) => question?.APIKey === keyName
    );
    return (
      (questionDetails &&
        questionDetails.options &&
        questionDetails.options.find((item) => item.isDefault)?.value) ||
      ''
    );
  };

  static getSelectedValueForQuestions = (
    questions: PricingQuestion[],
    keyName: string
  ) => {
    const questionDetails = questions.find(
      (question) => question?.APIKey === keyName
    );
    return (questionDetails && questionDetails.selectedValue) || '';
  };

  static getSelectedValueFromSession = (keyName: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return customerInfo[keyName] || '';
  };

  static isDriverAgeBelow24 = (cprNumber: string) => {
    return APIHelper.isCustomerUnderAge(cprNumber, 24);
  };

  static isCustomerUnderAge = (cprNumber: string, minAge: number) => ageResolver(cprNumber) < minAge;

  static getDefaultYearlyKilometer = (templateData: any) => {
    const yearlyKmData = templateData.productProperties.find(
      (properties: ITemplateOptions) => properties.keyName === 'yearlyKm'
    );
    return yearlyKmData?.options?.length > 0
      ? yearlyKmData.options[0].value
      : '';
  };
  static getDefaultSelection = (
    deductibleList: any,
    deductibleType: string
  ) => {
    const defaultSelfrisk: string = APIHelper.getSelectedPrice(
      deductibleList,
      deductibleType
    );
    return defaultSelfrisk;
  };
  static getDefaultValue = (data: any) =>
    data?.filter((item: any) => item.default).pop();
  static getSelectedPrice = (deductibleList: any, deductibleType: string) => {
    const templateOptions = deductibleList?.find(
      (properties: ITemplateOptions) => properties.keyName === deductibleType
    );
    const defaultSelectedOption = templateOptions.options.find(
      (option: ITemplateValues) => option.default
    );
    return defaultSelectedOption && defaultSelectedOption.value
      ? defaultSelectedOption.value
      : '';
  };

  static getCreateOfferPayload = (
    product: string,
    templateData: PricingStepConfig[]
  ) => {
    let payload;
    switch (product) {
      case PRODUCTS.CAR:
        payload = APIHelper.getCarCreateOfferPayload(templateData);
        break;
      case PRODUCTS.HOME:
        payload = APIHelper.getHomeCreateOfferPayload(templateData);
        break;
      case PRODUCTS.VILLA:
        payload = APIHelper.getVillaCreateOfferPayload();
        break;
      case PRODUCTS.MC:
        payload = APIHelper.getMcCreateOfferPayload(templateData);
        break;
      case PRODUCTS.ACCIDENT:
        payload = APIHelper.getAccidentCreateOfferPayload(templateData);
        break;
      case PRODUCTS.MOPED:
        payload = APIHelper.getMopedCreateOfferPayload(templateData);
        break;
      case PRODUCTS.PPI:
        payload = APIHelper.getPPICreateOfferPayload(templateData);
        break;
    }
    return payload;
  };

  static buildUpdateOfferRequest = (
    productName: string,
    offerDetails: any,
    key: string,
    value: string
  ) => {
    const updatedDetails = {
      policyNumber: offerDetails.policyNumber,
      agreementNumber: offerDetails.agreementNumber,
      attributeList: [
        {
          key,
          value
        }
      ]
    };
    return APIHelper.getUpdateOfferPayload(updatedDetails, productName);
  };

  static getUpdateOfferPayload = (updatedOption: any, productName: string) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    const defaultRequestParams = APIHelper.getDefaultCreatePayload(
      productName,
      customerInfo.cprNumber
    );
    return {
      ...defaultRequestParams,
      ...updatedOption
    };
  };

  static getBatchUpdatePayload = (
    offerDetails: Offer,
    modifiedInputs: ModifiedInputs,
    addedInputs?: AdditionalInputs[]
  ) => {
    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    return {
      headers: {
        Applikasjon: APP_CONSTANTS.application
      },
      body: {
        product: offerDetails.productName,
        policyNumber: offerDetails.policyNumber,
        channel: customerInfo?.channel
          ? customerInfo.channel
          : APP_CONSTANTS.channel,
        agreementNumber: offerDetails.agreementNumber,
        attributeList: modifiedInputs?.selections || [],
        extraBuildingList: APIHelper.buildAdditionalInputs(addedInputs) || []
      }
    };
  };

  static buildAdditionalInputs = (
    addedAdditionalInputs?: AdditionalInputs[]
  ) => {
    const extraBuildingList: any = [];
    if (addedAdditionalInputs && addedAdditionalInputs?.length > 0) {
      addedAdditionalInputs.map((item: AdditionalInputs) => {
        extraBuildingList.push({
          extraBuildingArea: item.area.value,
          extraFloors: item.type.value,
          extraFront: item.energyType.value
        });
      });
    }
    return extraBuildingList;
  };

  static buildCustomerSelections = (
    product: string,
    templateQuestions: PricingQuestion[],
    addedAdditionalInputs?: AdditionalInputs[]
  ) => {
    let customerSelections = {};
    switch (product) {
      case PRODUCTS.HOME:
        customerSelections = {
          area: parseInt(
            APIHelper.getSelectedValueForQuestions(templateQuestions, 'area')
          ),
          noOfPeople: APIHelper.getSelectedValueForQuestions(
            templateQuestions,
            'noOfPeople'
          )
        };
        break;
      case PRODUCTS.VILLA:
        customerSelections = {
          area: parseInt(
            APIHelper.getSelectedValueForQuestions(templateQuestions, 'area')
          ),
          roofConstruction: APIHelper.getSelectedValueForQuestions(
            templateQuestions,
            'roofConstruction'
          ),
          noOfPeople: APIHelper.getSelectedValueForQuestions(
            templateQuestions,
            'noOfPeople'
          ),
          builtYear: parseInt(
            APIHelper.getSelectedValueForQuestions(
              templateQuestions,
              'builtYear'
            )
          ),
          inspectionYear: parseInt(
            APIHelper.getSelectedValueForQuestions(
              templateQuestions,
              'inspectionYear'
            )
          ),
          energyType: APIHelper.getSelectedValueForQuestions(
            templateQuestions,
            'energyType'
          ),
          noOfFloors: APIHelper.getSelectedValueForQuestions(
            templateQuestions,
            'noOfFloors'
          ),
          basement: APIHelper.getSelectedValueForQuestions(
            templateQuestions,
            'basement'
          ),
          extraBuildingList: APIHelper.buildAdditionalInputs(
            addedAdditionalInputs
          )
        };
        break;
    }
    return customerSelections;
  };

  static saveSelectionsInSession = async (
    product: string,
    pricingQuestion: PricingStepConfig[],
    addedAdditionalInputs?: AdditionalInputs[]
  ) => {
    if (product === PRODUCTS.CAR || product === PRODUCTS.MC || product === PRODUCTS.MOPED) return;

    const customerInfo = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.CUSTOMER_INFO) || '{}'
    );
    const templateQuestions = pricingQuestion.flatMap(
      (template) => template.questions
    );
    const customerSelections = APIHelper.buildCustomerSelections(
      product,
      templateQuestions,
      addedAdditionalInputs
    );

    switch (product) {
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        sessionStorage.setItem(
          SESSION_KEYS.CUSTOMER_INFO,
          JSON.stringify({
            ...customerInfo,
            ...customerSelections
          })
        );
        break;
    }
  };
}
